import { format } from 'date-fns';
import { useMemo } from 'react';
import { Button } from '@tigergraph/app-ui-lib/button';
import { Checkbox } from '@tigergraph/app-ui-lib/checkbox';
import { ProgressBar } from '@tigergraph/app-ui-lib/progress-bar';
import { TableBuilder } from '@tigergraph/app-ui-lib/table';
import { useStyletron } from '@tigergraph/app-ui-lib/Theme';
import { Plus } from 'baseui/icon';
import { ColumnOverrides, TableBuilderColumn } from 'baseui/table-semantic';
import clsx from 'clsx';
import { expand } from 'inline-style-expand-shorthand';

import { actionColumnOverrides } from '@/components/table';
import { parseDate } from '@/lib/date';
import { formatBytes } from '@/utils/format';
import { StatusIndicator } from '@/pages/workgroup/tab/General';
import { SettingMenuForWorkSpace } from '@/pages/workgroup/tab/SettingMenu';
import { WorkspaceNamePopover } from '@/pages/workgroup/tab/general/WorkspaceNamePopover';
import { DatabaseT, WorkGroupT, WorkspaceT } from '@/pages/workgroup/type';

import { calculateUtilizationPercent, getWorkspaceFolderSize, parseMemoryToBytes } from './utils';
import { WorkspaceRow } from './types';

const centerColumnOverrides: ColumnOverrides = {
  TableHeadCell: { style: { verticalAlign: 'middle' } },
  TableBodyCell: { style: { verticalAlign: 'middle' } },
};

const centeredActionColumnOverrides: ColumnOverrides = {
  ...actionColumnOverrides,
  TableHeadCell: {
    ...actionColumnOverrides.TableHeadCell,
    style: {
      ...(actionColumnOverrides.TableHeadCell as any)?.style,
      verticalAlign: 'middle',
    },
  },
  TableBodyCell: {
    ...actionColumnOverrides.TableBodyCell,
    style: {
      ...(actionColumnOverrides.TableBodyCell as any)?.style,
      verticalAlign: 'middle',
    },
  },
};

export type TableViewProps = {
  group: WorkGroupT;
  rows: WorkspaceRow[];
  selectedIds: Set<string>;
  onToggleRow: (workspace: WorkspaceT) => void;
  onToggleAll: (selectAll: boolean) => void;
  isCreatingDisabled: boolean;
  canCreate: boolean;
  onCreate: () => void;
  emptyMessage?: string;
};

export default function TableView({
  group,
  rows,
  selectedIds,
  onToggleRow,
  onToggleAll,
  isCreatingDisabled,
  canCreate,
  onCreate,
  emptyMessage,
}: TableViewProps) {
  const [css, theme] = useStyletron();

  const allSelectableIds = rows.map((row) => row.workspace.workspace_id);
  const allChecked = allSelectableIds.length > 0 && allSelectableIds.every((id) => selectedIds.has(id));
  const someChecked = allSelectableIds.some((id) => selectedIds.has(id));
  const isIndeterminate = someChecked && !allChecked;

  const databaseById = useMemo(
    () =>
      group.tg_databases.reduce<Record<string, DatabaseT>>((acc, database) => {
        acc[database.database_id] = database;
        return acc;
      }, {}),
    [group.tg_databases]
  );

  return (
    <div
      className={css({
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        backgroundColor: theme.colors['background.alternative'],
        borderRadius: '12px',
        border: `1px solid ${theme.colors.divider}`,
        overflow: 'hidden',
      })}
    >
      <TableBuilder
        data={rows}
        emptyMessage={
          <div
            className={css({
              padding: '32px 16px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '12px',
              color: theme.colors['text.secondary'],
            })}
          >
            <span>{emptyMessage || 'No workspaces match your filters.'}</span>
            {canCreate ? (
              <Button
                kind="secondary"
                size="compact"
                disabled={isCreatingDisabled}
                onClick={onCreate}
                startEnhancer={<Plus size={16} />}
              >
                Create Workspace
              </Button>
            ) : null}
          </div>
        }
      >
        {/* Checkbox column */}
        <TableBuilderColumn<WorkspaceRow>
          id="select"
          overrides={{
            TableHeadCell: { style: { width: '48px', verticalAlign: 'middle' } },
            TableBodyCell: { style: { width: '48px', verticalAlign: 'middle' } },
          }}
          header={
            <Checkbox
              aria-label="Select all workspaces"
              checked={allChecked}
              isIndeterminate={isIndeterminate}
              onChange={(e) => onToggleAll((e.target as HTMLInputElement).checked)}
            />
          }
        >
          {(row: WorkspaceRow) => (
            <Checkbox
              aria-label={`Select workspace ${row.workspace.name}`}
              checked={selectedIds.has(row.workspace.workspace_id)}
              onChange={() => onToggleRow(row.workspace)}
            />
          )}
        </TableBuilderColumn>

        {/* Workspace / Database column */}
        <TableBuilderColumn<WorkspaceRow> id="name" header="WORKSPACE / DATABASE" overrides={centerColumnOverrides}>
          {(row: WorkspaceRow) => {
            const database = row.workspace.database_id ? databaseById[row.workspace.database_id] : undefined;
            const databaseName = database?.name || row.databaseName;
            return (
              <div
                className={css({
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                })}
              >
                <WorkspaceNamePopover
                  workspace={row.workspace}
                  database={database}
                  group={group}
                  className={css({
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: theme.colors['text.primary'],
                  })}
                >
                  {row.workspace.name}
                </WorkspaceNamePopover>
                {databaseName ? (
                  <span
                    className={css({
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontSize: '12px',
                      color: theme.colors['text.secondary'],
                    })}
                  >
                    {databaseName}
                  </span>
                ) : null}
              </div>
            );
          }}
        </TableBuilderColumn>

        {/* Status column */}
        <TableBuilderColumn<WorkspaceRow> id="status" header="STATUS" overrides={centerColumnOverrides}>
          {(row: WorkspaceRow) => (
            <div
              className={css({
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '12px',
                padding: '6px 12px',
                backgroundColor: theme.colors['tag.background.default'],
                borderRadius: '14px',
              })}
            >
              <StatusIndicator status={row.workspace.status} errMessage={row.workspace.err_message} size="compact" />
              <span>{row.workspace.status}</span>
            </div>
          )}
        </TableBuilderColumn>

        {/* Access column */}
        <TableBuilderColumn<WorkspaceRow> id="access" header="ACCESS" overrides={centerColumnOverrides}>
          {(row: WorkspaceRow) => (
            <span
              className={clsx(
                css({
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: '40px',
                  padding: '2px 8px',
                  borderRadius: '4px',
                  border: `1px solid ${row.workspace.is_rw ? '#BEDBFF' : '#E5E7EB'}`,
                  color: row.workspace.is_rw ? '#1447E6' : '#364153',
                  fontSize: '12px',
                  fontWeight: 500,
                  backgroundColor: row.workspace.is_rw ? '#EFF6FF' : '#F9FAFB',
                })
              )}
            >
              {row.workspace.is_rw ? 'R/W' : 'R/O'}
            </span>
          )}
        </TableBuilderColumn>

        {/* Topology Size column */}
        <TableBuilderColumn<WorkspaceRow> id="topologySize" header="TOPOLOGY SIZE" overrides={centerColumnOverrides}>
          {(row: WorkspaceRow) => (
            <div
              className={css({
                display: 'flex',
                flexDirection: 'column',
                gap: '2px',
              })}
            >
              <span
                className={css({
                  fontSize: '14px',
                  color: theme.colors['text.primary'],
                })}
              >
                {row.workspace.workspace_type?.typeName || '-'}
              </span>
              {row.workspace.workspace_type?.memory ? (
                <span
                  className={css({
                    fontSize: '12px',
                    color: theme.colors['text.secondary'],
                  })}
                >
                  {row.workspace.workspace_type.memory}
                </span>
              ) : null}
            </div>
          )}
        </TableBuilderColumn>

        {/* Data Utilization column */}
        <TableBuilderColumn<WorkspaceRow>
          id="dataUtilization"
          header="DATA UTILIZATION"
          overrides={centerColumnOverrides}
        >
          {(row: WorkspaceRow) => {
            const database = row.workspace.database_id ? databaseById[row.workspace.database_id] : undefined;
            return <DataUtilization workspace={row.workspace} database={database} />;
          }}
        </TableBuilderColumn>

        {/* Last Updated column */}
        <TableBuilderColumn<WorkspaceRow> id="lastUpdated" header="LAST UPDATED" overrides={centerColumnOverrides}>
          {(row: WorkspaceRow) => {
            const value = row.workspace.last_modified_time || row.workspace.created_at;
            if (!value) {
              return '-';
            }
            return format(parseDate(value), 'yyyy-MM-dd HH:mm');
          }}
        </TableBuilderColumn>

        {/* Actions column */}
        <TableBuilderColumn<WorkspaceRow> header="ACTIONS" overrides={centeredActionColumnOverrides}>
          {(row: WorkspaceRow) => (
            <div
              className={css({
                display: 'flex',
                gap: '6px',
              })}
            >
              <SettingMenuForWorkSpace
                workspace={row.workspace}
                database={row.workspace.database_id ? databaseById[row.workspace.database_id] : undefined}
                borderless
                showNavItems
              />
            </div>
          )}
        </TableBuilderColumn>
      </TableBuilder>
    </div>
  );
}

function DataUtilization({ workspace, database }: { workspace: WorkspaceT; database: DatabaseT | undefined }) {
  const [css, theme] = useStyletron();
  const totalBytes = parseMemoryToBytes(workspace.workspace_type?.memory);
  const usedBytes = getWorkspaceFolderSize(database, workspace.workspace_id);
  const percent =
    totalBytes !== undefined && usedBytes !== undefined
      ? calculateUtilizationPercent(usedBytes, totalBytes)
      : undefined;

  if (totalBytes === undefined || usedBytes === undefined) {
    return <span>-</span>;
  }

  return (
    <div
      className={css({
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        minWidth: '160px',
        maxWidth: '240px',
      })}
    >
      <div
        className={css({
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: '12px',
          color: theme.colors['text.secondary'],
        })}
      >
        <span>
          {formatBytes(usedBytes)} / {workspace.workspace_type.memory}
        </span>
        {percent !== undefined ? <span>{percent}%</span> : null}
      </div>
      <ProgressBar
        value={percent ?? 0}
        overrides={{
          BarContainer: {
            style: {
              marginLeft: 0,
              marginRight: 0,
            },
          },
          Bar: {
            style: {
              height: '10px',
              backgroundColor: '#E5E7EB',
              ...expand({
                borderRadius: '10px',
              }),
            },
          },
          BarProgress: {
            style: {
              backgroundColor: '#2B7FFF',
            },
          },
        }}
      />
    </div>
  );
}
